import { useEffect, useMemo, useRef } from "react";
import { FrameComponent } from "../../../../common/FrameComponent";
import { useDrawIoEditor } from "../../hooks/use-draw-io-editor";
import { useStorageSelectors } from "../../../../app/state/storageObjectSlice/hook/use-storage-selectors";
import { ExportSchemaBtn } from "../ExportSchemaBtn";
import { useAppMenus } from "../../../../app/state/menuSlice/hook/use-app-menus";
import { ExportDrawIoSchemaMenu } from "../ExportDrawIoSchemaMenu";
import { useAppSelector } from "../../../../services/redux/hooks/use-selector";
import { selectUserArtifactGroup } from "../../../Auth/redux/selectors";
import { DRAWIO_INITIAL_EMBED_URI, DRAWIO_VIEWER_URI } from "../../constants";
import { FrameOverlay } from "../FrameOverlay";
import { ImportDrawIoSchemaMenu } from "../ImportDrawIoSchemaMenu";
import { ImportSchemeBtn } from "../ImportSchemeBtn";
import { AppFloatButton } from "../../../../shared/components/AppFloatButton";
import { SparksIcon } from "../../../../shared/icons/SparksIcon";
import { SchemasChat } from "../SchemasChat";
import { UndoChangesXmlBtn } from "../UndoChangesXmlBtn";
import { SaveSchemaBtn } from "../SaveSchemaBtn";
import { useOpenChatDrawer } from "../../../../common/Chat/hook/use-open-chat-drawer";
import styles from "./styles.module.css";

export const DrawIoEditor = () => {
  const artifacts = useAppSelector(selectUserArtifactGroup);
  const iframeRef = useRef<HTMLIFrameElement>(null);
  const { selectedId, selectedObject } = useStorageSelectors("schemas");
  const { handleCloseMenu, handleOpenMenu } = useAppMenus();
  const {
    isReady,
    temporarySchema,
    loadXml,
    handleAutosaveThrottle,
    handleExportActions,
    handleUndoChangesXml,
    exportAsBase64,
  } = useDrawIoEditor(iframeRef);
  const { isChatOpen, handleCloseChatDrawer, handleOpenChatDrawer } =
    useOpenChatDrawer();

  const url = useMemo(() => {
    return !selectedObject?.write
      ? `${DRAWIO_VIEWER_URI}/#R${encodeURIComponent(selectedObject?.value?.schemas_object?.schema_xml ?? "")}`
      : DRAWIO_INITIAL_EMBED_URI;
  }, [selectedId, selectedObject?.write]);

  useEffect(() => {
    return () => handleCloseChatDrawer();
  }, [selectedId]);

  return (
    <div className={styles["schemas-container"]}>
      <FrameComponent key={selectedId} ref={iframeRef} src={url} />
      {artifacts && isReady && (
        <>
          <div className={styles["drawio-actions"]}>
            {temporarySchema && temporarySchema[selectedId ?? ""] && (
              <UndoChangesXmlBtn onClick={handleUndoChangesXml} />
            )}
            <SaveSchemaBtn
              onClick={(e) =>
                handleOpenMenu(e, "export_drawio_schema_menu", {
                  id: "",
                  value: "download",
                })
              }
              disabled={!selectedObject?.write}
            />
            <ImportSchemeBtn
              onClick={(e) =>
                handleOpenMenu(e, "import_drawio_schema_menu", null)
              }
              disabled={!selectedObject?.write}
            />
            <ExportSchemaBtn
              onClick={(e) =>
                handleOpenMenu(e, "export_drawio_schema_menu", {
                  id: "",
                  value: "artifacts",
                })
              }
              disabled={!selectedObject?.write}
            />
          </div>
          <ExportDrawIoSchemaMenu
            handleExport={handleExportActions}
            closeMenu={handleCloseMenu}
          />
          <ImportDrawIoSchemaMenu
            loadXml={loadXml}
            closeMenu={handleCloseMenu}
          />
        </>
      )}
      {isReady && !isChatOpen && (
        <AppFloatButton
          icon={
            <SparksIcon width={24} height={24} className={styles["icon"]} />
          }
          onClick={handleOpenChatDrawer}
          className={styles["chat-btn"]}
        />
      )}
      <FrameOverlay />
      {isReady && (
        <SchemasChat
          isChatOpen={isChatOpen}
          closeChat={handleCloseChatDrawer}
          exportAsBase64={exportAsBase64}
          loadXml={loadXml}
          handleAutosaveThrottle={handleAutosaveThrottle}
        />
      )}
    </div>
  );
};
